import { DateField } from "@keepoinc/site-page-react-component";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled, { css, ThemeProvider } from "styled-components";

import CalendarIcon from "../../icons/Calendar";
import ClockIcon from "../../icons/Clock";

/** The picker's colors in the editor: its dark frame and white text (the form's picker takes a Keepo's theme). */
const EDITOR_PICKER_THEME = { primary: "#181a20", secondary: "#ffffff" };

type Props = {
  id: string;
  /** Its accessible name (and the picker's). */
  label: string;
  kind: "DATE" | "TIME";
  /** "2026-10-20" for a date, "14:05" for a time; "" for none. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Shown while there's no value. */
  placeholder?: string;
  /** As tall as the editor's other fields (a form), or compact (a row of times, a toolbar). */
  size?: "field" | "compact";
};

/**
 * The editor's date and time field: the published form's own picker (the same calendar and lists of hours and
 * minutes, `DateField` of the Keepo's component), in the editor's colors and in a box like its other fields.
 */
export const DateTimeField = ({ id, label, kind, value, onChange, disabled, placeholder, size = "field" }: Props) => {
  const { i18n } = useTranslation();
  const frame = useRef<HTMLDivElement>(null);

  // The picker closes on Escape's key down and a modal around it on its key up: the one that closed the picker is
  // spent, so the modal (and what was typed in it) stays until the next Escape.
  useEffect(() => {
    let spent = false;
    const down = (event: KeyboardEvent) => {
      if (event.key === "Escape") spent = !!frame.current?.querySelector('[role="dialog"]');
    };
    const up = (event: KeyboardEvent) => {
      if (event.key === "Escape" && spent) {
        spent = false;
        event.stopPropagation();
      }
    };
    window.addEventListener("keydown", down, true);
    window.addEventListener("keyup", up, true);
    return () => {
      window.removeEventListener("keydown", down, true);
      window.removeEventListener("keyup", up, true);
    };
  }, []);

  return (
    <ThemeProvider theme={EDITOR_PICKER_THEME}>
      <Frame ref={frame} $size={size} $kind={kind} aria-disabled={disabled || undefined}>
        <DateField
          id={id}
          label={label}
          kind={kind}
          value={value}
          lang={i18n.language}
          inputProps={{ "aria-label": label, disabled, placeholder }}
          onChange={onChange}
          renderField={control => (
            <Box>
              {control}
              {/* the editor's own icons (as the agenda's menu), not the published form's */}
              <span className="icon" aria-hidden="true">
                {kind === "DATE" ? <CalendarIcon color="currentColor" /> : <ClockIcon color="currentColor" />}
              </span>
            </Box>
          )}
        />
      </Frame>
    </ThemeProvider>
  );
};

const Frame = styled.div<{ $size: "field" | "compact"; $kind: "DATE" | "TIME" }>`
  position: relative;
  display: inline-block;
  width: ${({ $size, $kind }) => ($size === "field" ? "100%" : $kind === "TIME" ? "6rem" : "9.5rem")};

  ${({ $size }) =>
    $size === "field"
      ? css`
          /* the editor's Input (inputCss): its border, corners, padding and type, pink when open */
          input {
            padding: 1rem 2.75rem 1rem 1rem;
            font-size: 0.875rem;
            font-weight: bold;
            line-height: 1rem;
          }

          .box {
            border: 0.125rem solid var(--color-neutral-light);
            border-radius: 0.625rem;
          }

          .box:hover {
            border-color: var(--color-neutral-light);
          }

          .box:focus-within {
            border-color: var(--color-brand-pink);
          }

          .icon {
            right: 1rem;
          }

          .icon svg {
            width: 20px;
            height: 20px;
          }
        `
      : css`
          input {
            height: 34px;
            padding: 0 2rem 0 0.625rem;
            font-size: 0.875rem;
          }
        `}

  /* the picker opens at its own size (a calendar needs room), not the box's width */
  && [role="dialog"] {
    right: auto;
    width: max-content;
    /* the time's lists are narrow, but its footer (Limpar, OK) needs the room */
    min-width: ${({ $kind }) => ($kind === "DATE" ? "18rem" : "10.5rem")};
  }

  /* an agenda's hours aren't "now": only Limpar and OK under the time's lists */
  ${({ $kind }) =>
    $kind === "TIME" &&
    css`
      && [role="dialog"] > :last-child > button:not(.clear):not(.done) {
        display: none;
      }
    `}

  /* the hours and the minutes in the middle of that width */
  && [role="dialog"] .time {
    flex: 1;
    gap: 1.5rem;
    justify-content: center;
  }

  &[aria-disabled="true"] {
    pointer-events: none;
    opacity: 0.5;
  }
`;

const Box = styled.div.attrs({ className: "box" })`
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  width: 100%;
  border: 1px solid var(--color-neutral-base);
  border-radius: 0.625rem;
  transition: border-color 0.15s;

  &:hover,
  &:focus-within {
    border-color: var(--color-neutral-150);
  }

  input {
    width: 100%;
    font-family: Inter, sans-serif;
    font-weight: 600;
    color: var(--color-neutral-white);
    font-variant-numeric: tabular-nums;
    cursor: pointer;
    background: transparent;
    border: 0;
    outline: none;
  }

  input::placeholder {
    color: var(--color-neutral-light);
  }

  .icon {
    position: absolute;
    right: 0.625rem;
    display: flex;
    color: var(--color-neutral-white);
    pointer-events: none;
  }

  .icon svg {
    width: 16px;
    height: 16px;
  }
`;
